import React, { ReactNode, useEffect, useState } from 'react';
import LoadingSpinner from 'components/LoadingSpinner';
import EmptyTableState from 'components/EmptyTableState';
import {
  OperationType,
  EMPTY_STATE_CONTENT
} from 'components/RecordsListView/constants';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import { useSummaryParams } from 'components/RecordsListView/context/SummaryParamsContext';
import { initialValues as defaultFilterValues } from 'components/RecordsListView/FilterDrawer/Constants/filterFormikData';
import FilterIcon from 'assets/filter_outlined_white.svg?react';
import {
  ADJUST_FILTERS_LABEL,
  CLEAR_FILTERS_LABEL
} from 'screens/Applications&WorkOrders/Components/Constants';
import { notifyDetailPanelReady } from './detailPanelReady';
import DetailPanelEmptyState from './DetailPanelEmptyState';

export interface DetailPanelProps {
  title: string;
  actionMenu?: ReactNode;
  children: ReactNode;
  isLoading?: boolean;
  isEmpty?: boolean;
  testId?: string;
  showSearchEmptyState?: boolean;
  showFilterEmptyState?: boolean;
  /**
   * Override the filter-empty-state "Adjust Filters"/"Clear Filters"
   * buttons, which otherwise default to the shared `SummaryParamsContext`.
   * Without this, Plans' filter buttons would silently update that inert
   * context instead of Plans' real filter drawer/state.
   */
  onAdjustFilters?: () => void;
  onClearFilters?: () => void;
  operationType: OperationType;
  // Layout classes default to the Applications & Work Orders styling; the
  // Invoices panel overrides them for its own header/content spacing.
  headerClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
}

const DEFAULT_HEADER_CLASSNAME =
  'flex items-center justify-between bg-white pb-15px p-4 border-b border-color-neutral-20';
const DEFAULT_TITLE_CLASSNAME =
  'text-2xl leading-8 font-semibold text-neutral-1000 truncate';
const DEFAULT_CONTENT_CLASSNAME = 'py-4 px-17px flex flex-col flex-1';

const DetailPanel = ({
  title,
  actionMenu,
  children,
  isLoading = false,
  isEmpty = false,
  showSearchEmptyState = false,
  showFilterEmptyState = false,
  onAdjustFilters,
  onClearFilters,
  testId = 'detail-panel',
  operationType,
  headerClassName = DEFAULT_HEADER_CLASSNAME,
  titleClassName = DEFAULT_TITLE_CLASSNAME,
  contentClassName = DEFAULT_CONTENT_CLASSNAME
}: Readonly<DetailPanelProps>) => {
  const { setIsDrawerVisible, handleFiltersChange } = useSummaryParams();
  const handleAdjustFilters =
    onAdjustFilters ?? (() => setIsDrawerVisible(true));
  const handleClearFilters =
    onClearFilters ?? (() => handleFiltersChange(defaultFilterValues));

  const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = panelElement;
    if (!element) {
      return undefined;
    }
    const publish = () => {
      const rect = element.getBoundingClientRect();
      const root = document.documentElement.style;
      root.setProperty('--detail-panel-left', `${Math.round(rect.left)}px`);
      root.setProperty('--detail-panel-width', `${Math.round(rect.width)}px`);
    };
    // ResizeObserver reports size, not position: scrolling the page sideways
    // moves the panel without resizing it, and the banner is position: fixed.
    // Capture phase so a scroll on any ancestor is seen, rAF so a scroll burst
    // costs one measurement per frame.
    let queued = false;
    const publishOnScroll = () => {
      if (queued) {
        return;
      }
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        publish();
      });
    };

    publish();
    notifyDetailPanelReady();
    const observer = new ResizeObserver(publish);
    observer.observe(element);
    window.addEventListener('resize', publish);
    window.addEventListener('scroll', publishOnScroll, true);
    // The box is deliberately left behind on unmount: a banner dismissed by
    // navigating away is still playing its leave animation, and clearing these
    // would drop it to the window-centred fallback mid-flight. The next panel
    // to mount overwrites them.
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', publish);
      window.removeEventListener('scroll', publishOnScroll, true);
    };
  }, [panelElement]);

  if (isLoading) {
    return (
      <div
        data-testid={testId}
        className="application-detail-panel flex-1 min-w-0 bg-neutral-10 flex justify-center items-center"
      >
        <LoadingSpinner className="flex items-center justify-center w-60 py-8 px-4 rounded-lg bg-white shadow-md" />
      </div>
    );
  }

  if (showSearchEmptyState) {
    return (
      <DetailPanelEmptyState
        title={EMPTY_STATE_CONTENT[operationType].search.title}
        content={EMPTY_STATE_CONTENT[operationType].search.content}
      />
    );
  }

  if (showFilterEmptyState) {
    return (
      <DetailPanelEmptyState
        title={EMPTY_STATE_CONTENT[operationType].filter.title}
        content={EMPTY_STATE_CONTENT[operationType].filter.content}
        actions={
          <div className="flex items-center gap-2 mt-4">
            <Button
              type={ButtonType.primary}
              icon={<FilterIcon />}
              onClick={handleAdjustFilters}
              size="large"
              className="text-sm"
            >
              {ADJUST_FILTERS_LABEL}
            </Button>
            <Button
              type={ButtonType.outline}
              onClick={handleClearFilters}
              size="large"
              className="text-sm"
            >
              {CLEAR_FILTERS_LABEL}
            </Button>
          </div>
        }
      />
    );
  }

  if (isEmpty) {
    return <EmptyTableState />;
  }

  return (
    <div
      ref={setPanelElement}
      data-testid={testId}
      className="application-detail-panel flex flex-col w-full bg-neutral-10 overflow-hidden"
      style={{ minHeight: 'calc(100vh - 63px)' }}
    >
      <header className={headerClassName}>
        <h2 className={titleClassName} title={title}>
          {title}
        </h2>
        {actionMenu}
      </header>
      <div className={contentClassName}>{children}</div>
    </div>
  );
};

export default DetailPanel;
